import AppContext from "@/AppContext";
import VideoPlayer from "@/components/VideoPlayer";
import Layout from "@/components/elements/Layout";
import ShareButtons from "@/components/parts/ShareButtons";
import FeaturedSlider from "@/components/report/FeaturedSlider";
import ReportPreview from "@/components/report/PreviewSlider";
import Schema from "@/components/schema/Schema";
import { useGetCurrentUrl } from "@/hooks";
import useAuth from "@/hooks/auth";
import { useDownloadReport } from "@/hooks/content";
import useWindowSize from "@/hooks/useWindowSize";
import { downloadReportWithForm, getContentDetailBySlug } from "@/services/api";
import { CONTENT_TYPES } from "@/utils/content";
import { formatDate } from "@/utils/datetime";
import {
  ArrowLeft,
  ArrowSquareOut,
  BookOpen,
  CaretRight,
  CircleNotch,
  DownloadSimple,
  Lock,
  LockKey,
  Megaphone,
  Play,
  X,
} from "@phosphor-icons/react";
import Head from "next/head";
import Image from "next/legacy/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useContext, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import ImageLogo from "../../../assets/images/nav-logo-vertical.svg";
// import { Dialog, Transition } from "@headlessui/react";
import SelectCountry from "@/components/SelectCountry";
import SelectIndustry from "@/components/SelectIndustry";
import SelectRole from "@/components/SelectRole";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  getConnectedContentsByName,
  getContentsByContentTypeName,
} from "@/services/api";
import { saveUserProfile } from "@/services/api/User";
import "@/utils/loader";
import dynamic from "next/dynamic";
import Script from "next/script";
import SubNavigation from "@/components/elements/SubNavigation";
import { loginPopup, purchasePopup } from "@/Atoms";
import { useAtom } from "jotai";
import ErrorMessage from "@/components/ErrorMessage";
import { saveDownloadForm } from "@/services/api";
import { subscribe } from "@/services/api/Newsletter";
import ModalActivateSubscription from "@/components/ModalActivateSubscription";
import ContentWishlistComponent from "@/components/parts/ContentWishlistComponent";
import FloatingQuota from "@/components/FloatingQuota";
import { useGetReports, refetchQuota } from "@/hooks/reports";

const CarouselInfobyte = dynamic(
  () => import("@/components/parts/CarouselInfobyte"),
  {
    ssr: false,
  }
);

const CarouselSpotlight = dynamic(
  () => import("@/components/parts/CarouselSpotlight"),
  {
    ssr: false,
  }
);
const CarouselArticle = dynamic(
  () => import("@/components/parts/CarouselArticle"),
  {
    ssr: false,
  }
);
const CarouselReport = dynamic(
  () => import("@/components/parts/CarouselReport"),
  {
    ssr: false,
  }
);
const CarouselVideo = dynamic(
  () => import("@/components/parts/CarouselVideo"),
  {
    ssr: false,
  }
);

const FormCompleteProfile = ({
  company,
  setCompany,
  firstName,
  setFirstName,
  lastName,
  setLastName,
  email,
  setEmail,
  country,
  setCountry,
  jobTitle,
  setJobTitle,
  role,
  setRole,
  industry,
  setIndustry,
  loadingDownload,
  user,
  isRead,
  report,
  handleCheckboxChange,
  errors,
  setIsLoginPopup,
  setDownloadFormOpen,
  setReadFormOpen,
}) => {
  const onOpenLogin = () => {
    setDownloadFormOpen(false);
    setReadFormOpen(false);
    setIsLoginPopup(true);
  };

  return (
    <div className='grid grid-cols-12 gap-4'>
      <div className='col-span-6 space-y-1.5'>
        <Label className='text-xs'>First Name</Label>
        <Input
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          disabled={loadingDownload}
          required
          className='bg-white text-background border-background/20 focus-visible:ring-0'
        />
        {errors.length > 0 && errors.includes("first_name") && (
          <ErrorMessage message='First name is required' />
        )}
      </div>
      <div className='col-span-6 space-y-1.5'>
        <Label className='text-xs'>Last Name</Label>
        <Input
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          disabled={loadingDownload}
          required
          className='bg-white text-background border-background/20 focus-visible:ring-0'
        />
        {errors.length > 0 && errors.includes("last_name") && (
          <ErrorMessage message='Last name is required' />
        )}
      </div>
      <div className='col-span-6 space-y-1.5'>
        <Label className='text-xs'>Email</Label>
        <Input
          type='email'
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className='bg-white text-background border-background/20 focus-visible:ring-0'
          disabled={user}
        />
        {errors.length > 0 && errors.includes("email") && (
          <ErrorMessage message='Email is required' />
        )}
      </div>
      <div className='col-span-6 space-y-1.5'>
        <Label className='text-xs'>Country of Residence</Label>
        <SelectCountry
          value={country}
          onChange={(value) => setCountry(value)}
          disabled={loadingDownload}
        />
        {errors.length > 0 && errors.includes("country") && (
          <ErrorMessage message='Country is required' />
        )}
      </div>
      <div className='col-span-6 space-y-1.5'>
        <Label className='text-xs'>Job Title</Label>
        <Input
          type='text'
          value={jobTitle}
          onChange={(e) => setJobTitle(e.target.value)}
          disabled={loadingDownload}
          required
          className='bg-white text-background border-background/20 focus-visible:ring-0'
        />
        {errors.length > 0 && errors.includes("job_title") && (
          <ErrorMessage message='Job title is required' />
        )}
      </div>
      <div className='col-span-6 space-y-1.5'>
        <Label className='text-xs'>Company</Label>
        <Input
          type='text'
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          disabled={loadingDownload}
          required
          className='bg-white text-background border-background/20 focus-visible:ring-0'
        />
        {errors.length > 0 && errors.includes("company") && (
          <ErrorMessage message='Company is required' />
        )}
      </div>
      <div className='col-span-6 space-y-1.5'>
        <Label className='text-xs'>Role</Label>
        <SelectRole
          value={role}
          onChange={(value) => setRole(value)}
          disabled={loadingDownload}
        />
        {errors.length > 0 && errors.includes("role") && (
          <ErrorMessage message='Role is required' />
        )}
      </div>
      <div className='col-span-6 space-y-1.5'>
        <Label className='text-xs'>Industry</Label>
        <SelectIndustry
          value={industry}
          onChange={(value) => setIndustry(value)}
          disabled={loadingDownload}
        />
        {errors.length > 0 && errors.includes("industry") && (
          <ErrorMessage message='Industry is required' />
        )}
      </div>
      <div className='col-span-12 text-xs leading-relaxed text-background/60'>
        {/* {!user && !isRead && (
          <p>
            I agree that my email will be used to automatically register to
            Lucidity Insights for having better experience.
          </p>
        )} */}
        {!user && (
          <p>
            if your email is already registered, please{" "}
            <span
              onClick={onOpenLogin}
              className='underline cursor-pointer text-background'
            >
              Login
            </span>{" "}
            instead
          </p>
        )}
        <div>
          <label className='flex items-start mt-2 gap-x-2'>
            {/* <Checkbox
              id='terms2'
              className='border border-background/20'
              onCheckedChange={handleCheckboxChange}
            /> */}
            <label htmlFor='terms2' className='text-xs'>
              By submitting your information, you will be automatically enrolled
              in our newsletter subscription to receive updates, news, and
              special offers. You can unsubscribe at any time by clicking on the
              unsubscribe link at the bottom of our emails.
            </label>
          </label>
        </div>
      </div>
    </div>
  );
};

function ExploredContent({ report, setIsOpen }) {
  return (
    <div>
      <h2 className='text-2xl font-bold tracking-tight text-background lg:text-4xl'>
        {report.title}
      </h2>
      {!report.is_coming_soon && (
        <div className='flex flex-wrap items-center gap-2 mt-5'>
          <p className='text-sm font-medium'>
            <span>Written by : </span>
            {report.authors.map((author, index) => (
              <Link
                key={index}
                href={`/author/${author.slug}`}
                className='no-underline'
              >
                <span className='underline'>{author.name}</span>
                {report.authors.length > 2 &&
                  index < report.authors.length - 2 &&
                  ", "}
                {report.authors.length > 1 &&
                  index === report.authors.length - 2 &&
                  " and "}
              </Link>
            ))}{" "}
            {/* <Link legacyBehavior href={`/author/${report.author.slug}`}>
              <a className='underline'>{report.author.name}</a>
            </Link> */}
          </p>
          <span className='hidden lg:inline-block'>&bull;</span>
          <p className='text-sm font-medium opacity-60'>
            {formatDate(report.publish_date, "MMMM DD, YYYY")}
          </p>
          <span className='hidden lg:inline-block'>&bull;</span>
          <ContentWishlistComponent item={report} />
        </div>
      )}
      <div className='h-full lg:flex'>
        <div
          dangerouslySetInnerHTML={{
            __html: report.description,
          }}
          className='flex-1 prose lg:pr-5 lg:mr-5 lg:border-r max-w-none border-black/10'
        ></div>
        <div className='lg:sticky top-0 lg:w-[200px] h-full'>
          <a href={`/reports/${report.slug}/read`}>
            <Image
              src={report.cover_image_url}
              width={215}
              height={290}
              layout={"responsive"}
              objectFit={"cover"}
              alt={report.thumbnail_alt}
              className='rounded-lg'
            />
          </a>
          <a
            href={`/reports/${report.slug}/read`}
            className='mt-2 w-full justify-center inline-flex items-center space-x-2.5 bg-gradient-to-r from-yellow-1 to-red-1 text-white font-medium text-sm px-7 py-3 rounded-md'
          >
            <BookOpen weight='bold' className='text-lg' />
            <span>Read Full Report</span>
          </a>
        </div>
      </div>
    </div>
  );
}

function ReportDetail({
  report,
  videos,
  infobytes,
  spotlights,
  articles,
  other_reports,
}) {
  const { state } = useContext(AppContext);
  const { planSubscription, loading, refreshUserSubscription } = state;
  const { user } = useAuth();
  const router = useRouter();
  const currentPath = router.asPath;
  const fullUrl = useGetCurrentUrl();
  // Older API responses only carry the single logo_url.
  const poweredByLogos = report.powered_by_logos?.length
    ? report.powered_by_logos
    : report.logo_url
      ? [{ url: report.logo_url, alt: report.logo_alt }]
      : [];
  const [isVideoPlaying, playVideo] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const { isDownloading, downloadReport } = useDownloadReport({ report });
  let [isOpen, setIsOpen] = useState(false);
  let [isLoginPopup, setIsLoginPopup] = useAtom(loginPopup);
  let [isOpenReport, setIsOpenReport] = useState(false);
  let [isLoaded, setIsLoaded] = useState(1);
  const [padding, setPadding] = useState(0);
  const containerRef = useRef(null);
  const size = useWindowSize();
  const [country, setCountry] = useState("");
  const [role, setRole] = useState("");
  const [industry, setIndustry] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [company, setCompany] = useState("");
  const [loadingDownload, setLoadingDownload] = useState(false);
  const [isChecked, setIsChecked] = useState(true);
  const [downloadFormOpen, setDownloadFormOpen] = useState(false);
  const [readFormOpen, setReadFormOpen] = useState(false);
  const [isRead, setIsRead] = useState(false);
  const [openLoginPopup, setOpenLoginPopup] = useAtom(loginPopup);
  const [openPurchasePopup, setOpenPurchasePopup] = useAtom(purchasePopup);
  const [errors, setErrors] = useState([]);
  const [loadingSaveForm, setLoadingSaveForm] = useState(false);
  const [isOpenActivateSubs, setIsOpenActivateSubs] = useState(false);
  const { quota, refetchQuota } = useGetReports({ onErrorCallback: () => {} });
  // Free accounts get a few downloads a month (the backend enforces it).
  // Downloading one of those again the same month is not counted twice.
  const isReportDownloaded = quota.downloadedUuids.includes(report.uuid);
  const canDownloadFree =
    !!user &&
    quota.isLoaded &&
    !quota.downloadUnlimited &&
    (quota.downloadRemaining > 0 || isReportDownloaded);

  const handleCheckboxChange = () => {
    // setIsChecked(!isChecked);
  };

  const onSubscribe = async () => {
    const thisYear = new Date().getFullYear();

    const payload = {
      email,
      tags: ["Customer", String(thisYear), "Web"],
    };

    if (firstName) {
      payload.first_name = firstName;
    }

    if (lastName) {
      payload.last_name = lastName;
    }

    try {
      await subscribe(payload);
    } catch (error) {
      console.log(error.response.data.message);
    }
  };

  const video_loading = false;
  const article_loading = false;
  const infobyte_loading = false;
  const spotlight_loading = false;
  const other_report_loading = false;

  useEffect(() => {
    setIsLoaded((s) => s + 1);
  }, [isOpenReport]);

  useEffect(() => {
    setCountry(user?.country || "");
    setRole(user?.role || "");
    setIndustry(user?.industry || "");
    setFirstName(user?.first_name || "");
    setLastName(user?.last_name || "");
    setJobTitle(user?.job_title || "");
    setCompany(user?.company_name || "");
    setEmail(user?.email || "");
  }, [user]);

  useEffect(() => {
    setTimeout(() => {
      let containerWidth = containerRef.current.offsetWidth - 32;
      if (size.width > 1024) {
        setPadding((size.width - containerWidth) / 2);
        setShowVideo(true);
      } else {
        setPadding(0);
        setShowVideo(false);
      }
    }, 200);
  }, [size]);

  useEffect(() => {
    if (!downloadFormOpen) {
      setIsRead(false);
    } else {
      setCountry(user?.country || "");
      setRole(user?.role || "");
      setIndustry(user?.industry || "");
      setFirstName(user?.first_name || "");
      setLastName(user?.last_name || "");
      setJobTitle(user?.job_title || "");
      setCompany(user?.company_name || "");
      setEmail(user?.email || "");
    }
  }, [downloadFormOpen]);

  useEffect(() => {
    if (!readFormOpen) {
      setIsRead(false);
    } else {
      setCountry(user?.country || "");
      setRole(user?.role || "");
      setIndustry(user?.industry || "");
      setFirstName(user?.first_name || "");
      setLastName(user?.last_name || "");
      setJobTitle(user?.job_title || "");
      setCompany(user?.company_name || "");
      setEmail(user?.email || "");
    }
  }, [readFormOpen]);

  // const [videos, video_loading] = useGetConnectedContents(
  //   CONTENT_TYPES.VIDEOS,
  //   report.uuid
  // );

  // const [infobytes, infobyte_loading] = useGetConnectedContents(
  //   CONTENT_TYPES.INFOBYTES,
  //   report.uuid
  // );
  // const [spotlights, spotlight_loading] = useGetConnectedContents(
  //   CONTENT_TYPES.SPOTLIGHTS,
  //   report.uuid
  // );
  // const [articles, article_loading] = useGetConnectedContents(
  //   CONTENT_TYPES.ARTICLES,
  //   report.uuid
  // );
  // const [other_reports, other_report_loading] = useGetContentsByContentType(
  //   CONTENT_TYPES.SPECIAL_REPORTS
  // );

  const goToLogin = () => {
    return setIsLoginPopup(true);
    // router.push(`/auth/login?redirect_to=${currentPath}`);
  };

  const renderPlayButton = () => {
    if (!report.video_url) return null;
    if (!user) {
      return (
        <button
          onClick={goToLogin}
          type='button'
          className='w-full md:w-auto text-white border border-white rounded-[15px] items-center inline-flex py-4 px-5 text-base lg:text-2xl font-bold gap-3 md:gap-4 hover:opacity-90 duration-300 ease-out hover:bg-white hover:text-background'
        >
          <Lock className='md:text-2xl' weight='fill' />
          Login to watch video
        </button>
      );
    }

    return (
      report.video_url && (
        <button
          onClick={() => playVideo(true)}
          className='w-full md:w-auto text-white border border-white rounded-[15px] items-center inline-flex py-4 px-5 text-base lg:text-2xl font-bold gap-3 md:gap-4 hover:opacity-90 duration-300 ease-out hover:bg-white hover:text-background'
        >
          <Play className='md:text-2xl' weight='fill' />
          Watch Video
        </button>
      )
    );
  };

  useEffect(() => {
    if (localStorage.getItem("nextStep") === "setDownloadFormOpen") {
      setDownloadFormOpen(true);
      localStorage.setItem("nextStep", "");
    }
  }, []);

  useEffect(() => {
    if (planSubscription) {
      setTimeout(() => {
        if (localStorage.getItem("nextStep") === "checkProfileCompleted") {
          handleDownload();
          localStorage.setItem("nextStep", "");
        }
      }, 2000);
    }
  }, [planSubscription]);

  useEffect(() => {
    refetchQuota();
  }, [report]);

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": fullUrl,
      },
      headline: report.meta_title,
      description: report.meta_desc,
      image: report.thumbnail_url,
      //  author: {
      //    "@type": "Person",
      //    name: "Erica",
      //    url: "https://lucidityinsights.com/author/erica",
      //  },
      publisher: {
        "@type": "Organization",
        name: "Lucidity Insights",
        logo: {
          "@type": "ImageObject",
          url: ImageLogo.src,
        },
      },
      datePublished: report.publish_date,
      dateModified: report.publish_date,
    },
    {
      "@context": "https://schema.org/",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: `${process.env.BASE_URL}`,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Reports",
          item: `${process.env.BASE_URL}reports`,
        },
        {
          "@type": "ListItem",
          position: 3,
          name: report.title,
          item: `${process.env.BASE_URL}reports/${router.query.slug}`,
        },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "VideoObject",
      name: report.meta_title,
      description: report.meta_desc,
      thumbnailUrl: report.thumbnail_url,
      uploadDate: report.publish_date,
      // "duration": "",
      contentUrl: report.video_bg_url,
      // "embedUrl": "",
      publisher: {
        "@type": "Organization",
        name: "Lucidity Insights",
        logo: {
          "@type": "ImageObject",
          url: ImageLogo.src,
        },
      },
    },
  ];

  const triggerDownload = (url, filename = 'report.pdf') => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const onDownloadWithForm = async () => {
    if (loadingDownload) return;
    setIsRead(true);

    setErrors([]);
    const payload = {
      first_name: firstName,
      last_name: lastName,
      company: company,
      job_title: jobTitle,
      role,
      industry,
      country,
      url: location.href,
      report_uuid: report.uuid,
    };

    let completed = true;
    for (const key in payload) {
      if (!payload[key]) {
        setErrors((state) => [...state, key]);
        completed = false;
      }
    }

    if (!completed) {
      return;
    }

    if (user) {
      await saveUserProfile({ ...payload, company_name: payload.company });
    }

    setTimeout(async () => {
      const thisYear = new Date().getFullYear();

      setLoadingDownload(true);
      try {
        const res = await downloadReportWithForm(report.uuid, {
          first_name: firstName,
          last_name: lastName,
          email,
          country,
          job_title: jobTitle,
          company,
          role,
          industry,
          subscribeNewsletter: isChecked,
          url: location.href,
          report_uuid: report.uuid,
          tags: ["Customer", String(thisYear), "Special Report"],
        });
        setLoadingDownload(false);
        setIsLoginPopup(false);
        setDownloadFormOpen(false);

        // Trigger direct download using the download_url from API response
        if (res.data?.success && res.data?.download_url) {
          triggerDownload(res.data.download_url, `${report.title}.pdf`);
          toast.success('Your download has started!');
          refetchQuota();
        } else {
          toast.error('Download failed. Please try again.');
        }

        if (
          planSubscription &&
          planSubscription.has_active_subscription &&
          planSubscription.status === "trialing"
        ) {
          refreshUserSubscription();
        }
      } catch (error) {
        setLoadingDownload(false);
        if (error?.response?.data?.limit_reached) {
          toast.error(error.response.data.message);
          refetchQuota();
        } else {
          toast.error('Download failed. Please try again.');
        }
      }
    }, 100);
  };

  const onReadFullReport = () => {
    if (!user && !report.is_latest_report) {
      return setOpenLoginPopup(true);
      // return router.push(`/auth/login?redirect_to=${currentPath}`);
    }

    setIsRead(true);
    setTimeout(() => {
      const payload = {
        first_name: firstName,
        last_name: lastName,
        company: company,
        job_title: jobTitle,
        role,
        industry,
        country,
        url: location.href,
        report_uuid: report.uuid,
      };

      let completed = false;

      for (const key in payload) {
        if (!payload[key]) {
          completed = false;
          break;
        } else {
          completed = true;
        }
      }

      if (!completed) {
        setReadFormOpen(true);
      } else {
        location.href = `/reports/${report.slug}/read`;
      }
    }, 100);
  };

  const handleDownload = () => {
    setTimeout(() => {
      const payload = {
        first_name: firstName,
        last_name: lastName,
        company: company,
        job_title: jobTitle,
        role,
        industry,
        country,
        url: location.href,
        report_uuid: report.uuid,
      };

      let completed = false;

      for (const key in payload) {
        if (!payload[key]) {
          completed = false;
          break;
        } else {
          completed = true;
        }
      }

      if (
        !completed
        // || !(planSubscription && planSubscription.has_active_subscription)
      ) {
        setDownloadFormOpen(true);
      } else {
        onDownloadWithForm();
      }
    }, 100);
  };

  const checkProfileCompleted = () => {
    if (!user) {
      // localStorage.setItem('nextStep', 'setDownloadFormOpen')
      return setOpenLoginPopup(true);
      // return router.push(`/auth/login?redirect_to=${currentPath}`);
    }

    if (
      (planSubscription &&
        planSubscription.has_active_subscription &&
        planSubscription.status !== "trialing") ||
      user?.roles === "admin-team" ||
      user?.roles === "read-access"
    ) {
      setIsRead(false);
      handleDownload();
    } else {
      setDownloadFormOpen(true);
    }
  };

  const onSaveProfile = async () => {
    console.log("onSaveProfile");
    setErrors([]);
    setLoadingSaveForm(true);
    try {
      const payload = {
        first_name: firstName,
        last_name: lastName,
        company: company,
        job_title: jobTitle,
        role,
        industry,
        country,
        url: location.href,
        report_uuid: report.uuid,
      };

      let completed = true;
      for (const key in payload) {
        if (!payload[key]) {
          setErrors((state) => [...state, key]);
          completed = false;
        }
      }

      if (!completed) {
        setLoadingSaveForm(false);
        return;
      }

      if (isChecked) {
        await onSubscribe();
      }

      if (user) {
        await saveUserProfile({ ...payload, company_name: payload.company });
        try {
          setLoadingSaveForm(false);
          setReadFormOpen(false);
          location.href = `/reports/${report.slug}/read`;
        } catch (error) {
          console.log(error);
        }
      } else {
        try {
          payload["email"] = email;
          const response = await saveDownloadForm(payload);
          console.log(response);
          location.href = `/reports/${report.slug}/read?access_token=${response.data}`;
        } catch (error) {
          console.log(error);
        }
      }
      setLoadingSaveForm(false);
    } catch (error) {
      console.log(error);
    }
  };

  const onOpenPayment = () => {
    setDownloadFormOpen(false);
    // setOpenPurchasePopup("content");
    router.push(`/pricing`);
    // localStorage.setItem("nextStep", "checkProfileCompleted");
  };

  return (
    <div>
      <Head>
        <meta
          property='article:published_time'
          content={new Date(report.publish_date).toISOString()}
        />
        <meta
          property='article:modified_time'
          content={new Date(report.publish_date).toISOString()}
        />
        <meta property='article:section' content='Article Section' />
      </Head>
      <Layout
        title={report.title}
        meta_title={report.meta_title}
        description={report.meta_desc}
        meta_image={report.cover_image_url}
      >
        <link
          href='/pflip/css/pdfflip.css'
          rel='stylesheet'
          type='text/css'
        ></link>
        <Script src='/pflip/js/libs/jquery.min.js'></Script>
        <Script src='/pflip/js/pdfflip.js'></Script>
        <Script src='/pflip/settings.js'></Script>
        <Schema data={structuredData} />
        <SubNavigation title={report.title}>
          <div className='inline-flex items-center ml-auto space-x-2'>
            <span className='text-[11px] font-medium tracking-wider uppercase opacity-60'>
              Share
            </span>
            <span className='border-t w-7 border-white/20'></span>
            <ShareButtons
              title={report.title}
              description={report.meta_desc}
              url={fullUrl}
            />
          </div>
        </SubNavigation>

        {user && planSubscription && !planSubscription.has_active_subscription && <FloatingQuota quota={quota} />}

        <header className='overflow-hidden'>
          <div className='relative mt-12 mb-24 lg:mt-20 lg:mb-32'>
            {report.video_bg_url && showVideo ? (
              <>
                <video
                  autoPlay
                  loop
                  muted
                  className='absolute inset-0 hidden object-cover w-full h-full lg:block'
                  poster={report.background_image_url}
                  preload='none'
                >
                  <source
                    id='video'
                    src={report.video_bg_url}
                    type='video/mp4'
                  />
                </video>
                <img
                  className='absolute inset-0 object-cover w-full h-full lg:hidden'
                  src={report.background_image_url}
                  alt='Video Background Image'
                />
              </>
            ) : (
              <img
                className='absolute inset-0 object-cover w-full h-full'
                src={report.background_image_url}
                alt='Video Background Image'
              />
            )}
            <span className='absolute inset-0 w-full h-full bg-gradient-to-b from-background via-background/10 to-background'></span>
            <span className='absolute inset-0 w-full h-full bg-gradient-to-r from-background via-background/10 to-background'></span>
            <div
              ref={containerRef}
              className='container relative z-10 text-white'
            >
              <div className='lg:flex'>
                <div className='lg:w-[55%]'>
                  <div className='flex items-center justify-between mb-8 lg:mb-10 lg:hidden'>
                    {report.video_url && (
                      <button
                        type='button'
                        onClick={() => playVideo(true)}
                        className='inline-flex items-center justify-start space-x-3 lg:-translate-y-16'
                      >
                        <div className='relative'>
                          <div className='inline-flex items-center justify-center w-16 h-16 rounded-full pulse bg-gradient-to-r from-orange-1 to-red-1'>
                            <Play size={24} weight='fill'></Play>
                          </div>
                        </div>
                        <div className='flex flex-col text-left space-y-0.5'>
                          <span className='text-lg font-medium tracking-wide uppercase'>
                            Watch
                          </span>
                          <span className='text-xs tracking-wide opacity-60'>
                            Trailer
                          </span>
                        </div>
                      </button>
                    )}
                  </div>
                  {!!report.is_coming_soon && (
                    <span className='inline-flex items-center px-2.5 py-1.5 mb-6 space-x-1 text-xs font-medium leading-none tracking-tight rounded-full shadow bg-gradient-to-tr from-yellow-1 to-red-1'>
                      <Megaphone weight='bold' className='text-sm' />
                      <span>Coming Soon</span>
                    </span>
                  )}
                  <h1 className='text-4xl font-bold tracking-tight lg:leading-[1.1] lg:text-5xl'>
                    {report.title}
                  </h1>

                  {!report.is_coming_soon && (
                    <div className='flex flex-wrap items-center gap-2 mt-5 mb-2'>
                      <p className='text-sm font-medium'>
                        <span>Written by : </span>
                        {report.authors.map((author, index) => (
                          <Link
                            key={index}
                            href={`/author/${author.slug}`}
                            className='no-underline'
                          >
                            <span className='underline'>{author.name}</span>
                            {report.authors.length > 2 &&
                              index < report.authors.length - 2 &&
                              ", "}
                            {report.authors.length > 1 &&
                              index === report.authors.length - 2 &&
                              " and "}
                          </Link>
                        ))}{" "}
                      </p>
                      <span className='hidden lg:inline-block'>&bull;</span>
                      <p className='text-sm font-medium opacity-60'>
                        {formatDate(report.publish_date, "MMMM DD, YYYY")}
                      </p>
                      {/* <span className='hidden lg:inline-block'>&bull;</span> */}
                      <ContentWishlistComponent item={report} />
                    </div>
                  )}
                  <div className='mb-4 lg:hidden'>
                    <ShareButtons
                      title={report.title}
                      description={report.meta_desc}
                      url={fullUrl}
                    />
                  </div>
                  {poweredByLogos.length > 0 && (
                    <div className='mt-5 mb-5 lg:mb-8'>
                      <span className='block mb-3 text-xs opacity-60'>
                        Powered by :
                      </span>
                      <div className='flex flex-wrap items-center gap-x-6 gap-y-3'>
                        {poweredByLogos.map((logo) => (
                          <img
                            key={logo.url}
                            src={logo.url}
                            className='h-9 lg:h-10'
                            alt={logo.alt || "Report Powered Company Logo"}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  <p className='mb-4 text-lg opacity-60'>{report.excerpt}</p>
                  {!report.is_coming_soon && (
                    <>
                      {report.description && (
                        <div className='flex items-center space-x-4'>
                          <Dialog>
                            <DialogTrigger asChild>
                              <button className='flex space-x-2.5 items-center underline'>
                                <span className='whitespace-nowrap'>
                                  Explore Content
                                </span>
                                <ArrowSquareOut
                                  weight='bold'
                                  className='text-sm'
                                />
                              </button>
                            </DialogTrigger>
                            <DialogContent className='max-w-5xl p-0 bg-white text-background'>
                              <ScrollArea className='h-[90vh] p-5 lg:p-8'>
                                <ExploredContent
                                  report={report}
                                  setIsOpen={setIsOpen}
                                />
                              </ScrollArea>
                            </DialogContent>
                          </Dialog>
                        </div>
                      )}
                      <div className='inline-flex flex-wrap items-center w-full mt-8 space-y-2 lg:space-y-0 lg:space-x-2 lg:flex-nowrap lg:w-auto'>
                        <Dialog
                          open={downloadFormOpen}
                          onOpenChange={setDownloadFormOpen}
                        >
                          <button
                            onClick={checkProfileCompleted}
                            type='button'
                            className={`w-full lg:w-auto inline-flex items-center space-x-2.5 text-sm lg:text-base bg-gradient-to-r text-white font-medium px-4 justify-center lg:px-7 py-3 rounded-md ${
                              loadingDownload
                                ? "animate-pulse from-slate-600 to-slate-500"
                                : "from-yellow-1 to-red-1"
                            }`}
                            disabled={loadingDownload}
                          >
                            <span className='whitespace-nowrap'>
                              {loadingDownload
                                ? "Downloading..."
                                : "Download Report"}
                            </span>
                            {loadingDownload ? (
                              <CircleNotch
                                weight='bold'
                                className='text-xl animate-spin'
                              />
                            ) : (
                              <DownloadSimple
                                weight='bold'
                                className='text-lg'
                              />
                            )}
                          </button>
                          <DialogContent className='bg-white text-background'>
                            {(planSubscription &&
                              planSubscription.has_active_subscription &&
                              planSubscription.status !== "trialing") ||
                            (planSubscription &&
                              planSubscription.has_active_subscription &&
                              planSubscription.status == "trialing" &&
                              planSubscription.user_downloads_count < 3) ||
                            user?.roles === "admin-team" ||
                            user?.roles === "read-access" ||
                            canDownloadFree ? (
                              <>
                                <FormCompleteProfile
                                  {...{
                                    company,
                                    setCompany,
                                    firstName,
                                    setFirstName,
                                    lastName,
                                    setLastName,
                                    email,
                                    setEmail,
                                    country,
                                    setCountry,
                                    jobTitle,
                                    setJobTitle,
                                    role,
                                    setRole,
                                    industry,
                                    setIndustry,
                                    loadingDownload,
                                    user,
                                    isRead,
                                    report,
                                    handleCheckboxChange,
                                    errors,
                                    setIsLoginPopup,
                                    setDownloadFormOpen,
                                    setReadFormOpen,
                                  }}
                                />
                                <Button
                                  variant='secondary'
                                  onClick={onDownloadWithForm}
                                  disabled={loadingDownload}
                                >
                                  {loadingDownload
                                    ? "Downloading..."
                                    : "Download Report"}
                                </Button>
                                {canDownloadFree && !isReportDownloaded && (
                                  <p className='-mt-2 text-xs text-center opacity-60'>
                                    {quota.downloadRemaining} of{" "}
                                    {quota.downloadTotal} free downloads left
                                    this month
                                  </p>
                                )}
                              </>
                            ) : planSubscription &&
                              planSubscription.has_active_subscription &&
                              planSubscription.status == "trialing" &&
                              planSubscription.user_downloads_count > 2 ? (
                              <div className='text-center'>
                                {/* <span className='inline-block mb-3 text-5xl'>
                                ⚡️
                              </span> */}
                                <LockKey className='mx-auto mb-2 text-5xl text-orange-1' />
                                <p className='mb-2 text-2xl font-semibold'>
                                  Activate Full Subscription to Continue
                                </p>
                                <p className='mb-3 text-sm opacity-70'>
                                  You have reached the limit for downloading
                                  reports. Please activate a full subscription
                                  to enjoy unlimited downloads.
                                </p>
                                <Button
                                  onClick={() => {
                                    setDownloadFormOpen(false);
                                    setIsOpenActivateSubs(true);
                                  }}
                                  variant='secondary'
                                  className='w-full'
                                >
                                  ⚡️ Activate Full Subscription
                                </Button>
                              </div>
                            ) : (
                              <div className='text-center'>
                                {/* <span className='inline-block mb-3 text-5xl'>
                                ⚡️
                              </span> */}
                                <LockKey className='mx-auto mb-2 text-5xl text-orange-1' />
                                <p className='mb-2 text-2xl font-semibold'>
                                  {quota.isLoaded && !quota.downloadUnlimited
                                    ? "You've reached your monthly limit"
                                    : "Subscribe to continue"}
                                </p>
                                <p className='mb-5 text-sm opacity-70'>
                                  {quota.isLoaded && !quota.downloadUnlimited
                                    ? `Free accounts can download ${quota.downloadTotal} special reports per month. Upgrade your plan for unlimited downloads.`
                                    : "To download this report, please subscribe to a plan"}
                                </p>
                                <Button
                                  onClick={onOpenPayment}
                                  variant='secondary'
                                  className='w-full'
                                >
                                  {quota.isLoaded && !quota.downloadUnlimited
                                    ? "⚡️ Upgrade Plan"
                                    : "⚡️ Subscribe"}
                                </Button>
                              </div>
                            )}
                          </DialogContent>
                        </Dialog>

                        <Dialog
                          open={readFormOpen}
                          onOpenChange={setReadFormOpen}
                        >
                          <button
                            onClick={onReadFullReport}
                            className='w-full lg:w-auto inline-flex space-x-2.5 items-center text-sm lg:text-base font-medium px-4 justify-center lg:px-7 py-3 border-[1.5px] rounded-md border-orange-1'
                          >
                            <span className='whitespace-nowrap'>
                              Read Full Report
                            </span>
                            <BookOpen weight='bold' className='text-lg' />
                          </button>
                          <DialogContent className='bg-white text-background'>
                            <FormCompleteProfile
                              {...{
                                company,
                                setCompany,
                                firstName,
                                setFirstName,
                                lastName,
                                setLastName,
                                email,
                                setEmail,
                                country,
                                setCountry,
                                jobTitle,
                                setJobTitle,
                                role,
                                setRole,
                                industry,
                                setIndustry,
                                loadingDownload,
                                user,
                                isRead,
                                report,
                                handleCheckboxChange,
                                errors,
                                setIsLoginPopup,
                                setDownloadFormOpen,
                                setReadFormOpen,
                              }}
                            />
                            <Button
                              variant='secondary'
                              onClick={onSaveProfile}
                              disabled={loadingSaveForm}
                            >
                              {loadingSaveForm
                                ? "Submitting..."
                                : "Submit Data"}
                            </Button>
                          </DialogContent>
                        </Dialog>
                        <div className='!pl-5 !ml-7 border-l border-white/10 hidden lg:block'>
                          {report.video_url && (
                            <button
                              type='button'
                              onClick={() => playVideo(true)}
                              className='inline-flex items-center space-x-3'
                            >
                              <div className='relative'>
                                <div className='inline-flex items-center justify-center w-16 h-16 rounded-full pulse bg-gradient-to-r from-orange-1 to-red-1'>
                                  <Play size={24} weight='fill'></Play>
                                </div>
                              </div>
                              <div className='flex flex-col items-start space-y-0.5'>
                                <span className='text-lg font-medium tracking-wide uppercase'>
                                  Watch
                                </span>
                                <span className='text-xs tracking-wide opacity-60'>
                                  Trailer
                                </span>
                              </div>
                            </button>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                  {!!report.is_coming_soon && (
                    <div className='flex-1 lg:w-auto inline-flex text-white/80 space-x-2 items-center text-sm lg:text-base font-medium px-4 justify-center lg:px-7 py-3 border-[1.5px] rounded-md border-orange-1/40 bg-white/5'>
                      <Megaphone weight='bold' className='text-lg' />
                      <span className='whitespace-nowrap'>
                        Report is Coming Soon...
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className={"right-0 mt-10 lg:absolute lg:w-2/5 lg:mt-0 top-0"}>
              {/* <div className='justify-center hidden text-white lg:flex'>
                {report.video_url && (
                  <button
                    type='button'
                    onClick={() => playVideo(true)}
                    className='relative z-20 inline-flex items-center space-x-3 -translate-y-24'
                  >
                    <div className='relative'>
                      <div className='inline-flex items-center justify-center w-16 h-16 rounded-full pulse bg-gradient-to-r from-orange-1 to-red-1'>
                        <Play size={24} weight='fill'></Play>
                      </div>
                    </div>
                    <div className='flex flex-col items-start space-y-0.5'>
                      <span className='text-lg font-medium tracking-wide uppercase'>
                        Watch
                      </span>
                      <span className='text-xs tracking-wide opacity-60'>
                        Trailer
                      </span>
                    </div>
                  </button>
                )}
              </div> */}
              {report.preview_images.length > 0 ? (
                <ReportPreview
                  cover={report.cover_image_url}
                  pages={report.preview_images}
                />
              ) : (
                <div className='container relative'>
                  <div className='-mb-52 self-center hidden w-full md:block max-w-md md:order-last rounded-[15px] relative'>
                    <div className='z-10 p-2px rounded-[15px] top-1 left-1 absolute w-full h-full opacity-100 bg-gradient-to-r from-yellow-1 to-red-1 mb-4'>
                      <div className='z-10 rounded-[14px] w-full h-full bg-transparent'></div>
                    </div>
                    <div className='z-30 relative rounded-[15px] h-full bg-no-repeat bg-cover bg-center shadow-sm'>
                      <div className='relative w-full h-auto'>
                        <Image
                          src={report.cover_image_url}
                          width={215}
                          height={290}
                          layout={"responsive"}
                          objectFit={"cover"}
                          alt={report.thumbnail_alt}
                          className='rounded-[15px]'
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {report.featured_items.length > 0 && (
              <div className='container relative items-center h-16 mt-10 text-white lg:mt-16'>
                <span className='flex-shrink-0 block mb-4 text-sm opacity-60'>
                  Featured :{" "}
                </span>
                <FeaturedSlider logos={report.featured_items}></FeaturedSlider>
              </div>
            )}
          </div>
        </header>

        {!report.is_coming_soon && (
          <div className='container flex flex-wrap space-x-16 lg:flex-nowrap mb-14 lg:mb-24'>
            <div className='w-full space-y-14 lg:space-y-24'>
              {(videos.length > 0 || video_loading) && (
                <CarouselVideo
                  title='Videos'
                  data={videos}
                  videoHeight={"h-[250px]"}
                  showMoreLink={`/videos`}
                  showMoreText='See All Videos'
                />
              )}
              {(infobytes.length > 0 || infobyte_loading) && (
                <CarouselInfobyte
                  title='Infobytes'
                  data={infobytes}
                  showMoreLink={`/infobytes`}
                  showMoreText='See All Infobytes'
                />
              )}
              {(spotlights.length > 0 || spotlight_loading) && (
                <CarouselSpotlight
                  title='Spotlights'
                  data={spotlights}
                  cardHeight='h-[250px]'
                  showMoreLink={`/spotlights`}
                  showMoreText='See All Spotlights'
                />
              )}
              {(articles.length > 0 || article_loading) && (
                <CarouselArticle
                  title='Articles'
                  data={articles}
                  cardHeight='h-[250px]'
                  showMoreLink={`/articles`}
                  showMoreText='See All Articles'
                />
              )}
              <CarouselReport
                title='Special Reports'
                data={other_reports.filter(
                  (other) => other.uuid !== report.uuid
                )}
                cardHeight={"h-[250px]"}
                showMoreLink={`/reports`}
                showMoreText='Browser Other Reports'
              />
            </div>
            {/* <div className='lg:w-1/5'>
            <a
              className='block relative aspect-[3/4] w-full'
              href={`/reports/${report.slug}/read`}
            >
              <Image
                src={report.cover_image_url}
                layout='fill'
                objectFit='cover'
                alt={report.thumbnail_alt}
                className='rounded-lg'
              />
              <div className='absolute flex items-center space-x-2.5 right-3 top-3'>
                <ArrowSquareOut weight='bold' />
                <span className='bg-gradient-to-tr from-yellow-1 to-red-1 rounded inline-block py-0.5 px-2 text-xs font-medium tracking-wide'>
                  Ads
                </span>
              </div>
            </a>
          </div> */}
          </div>
        )}

        {report.video_url && isVideoPlaying && (
          <VideoPlayer
            url={report.video_url}
            onClose={() => playVideo(false)}
            playerName="Report Video"
            videoId={report.id}
            videoTitle={report.title}
          />
        )}
      </Layout>
      <div className='max-w-6xl px-4 mx-auto mb-8 2xl:max-w-7xl'>
        <div
          id='staticModal'
          data-modal-backdrop='static'
          tabIndex='-1'
          aria-hidden='true'
          className={`fixed left-0 top-10 z-50 p-4 overflow-x-hidden overflow-y-hidden md:inset-0 h-modal md:h-full ${
            isOpenReport ? "" : "hidden"
          }`}
        >
          <div className='relative w-full h-full p-10 md:h-auto'>
            <div className='relative rounded-lg shadow dark:bg-gray-700'>
              <div className='relative space-y-6 rounded-lg'>
                <button
                  type='button'
                  className='text-white z-20 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center dark:hover:bg-gray-600 dark:hover:text-white absolute top-0 right-0 m-2'
                  data-modal-hide='staticModal'
                  onClick={() => setIsOpenReport(false)}
                >
                  <svg
                    className='w-5 h-5'
                    fill='currentColor'
                    viewBox='0 0 20 20'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path
                      fillRule='evenodd'
                      d='M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z'
                      clipRule='evenodd'
                    ></path>
                  </svg>
                </button>
                <button
                  // disabled={loadingDownload}
                  // onClick={onDownload}
                  className='bg-gradient-to-r from-orange-1 to-red-1 text-white text-sm px-3 py-1.5 rounded flex gap-2 items-center justify-center mb-4 hover:opacity-90 absolute z-20 right-10 top-3'
                >
                  Download Report
                  <DownloadSimple size={20} />
                </button>
                <div
                  className='rounded-lg PDFFlip'
                  id='PDFF'
                  source={report.file_url}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ModalActivateSubscription
        isOpen={isOpenActivateSubs}
        setIsOpen={setIsOpenActivateSubs}
        planSubscription={planSubscription}
      />
    </div>
  );
}

export async function getServerSideProps({ query }) {
  try {
    const response = await getContentDetailBySlug(query.slug, {
      params: {
        contentTypeName: CONTENT_TYPES.SPECIAL_REPORTS,
      },
    });
    const report = response.data.data;

    const reportData = await getContentsByContentTypeName(
      CONTENT_TYPES.SPECIAL_REPORTS,
      report.uuid
    );

    const videoData = await getConnectedContentsByName(
      CONTENT_TYPES.VIDEOS,
      report.uuid
    );

    const articleData = await getConnectedContentsByName(
      CONTENT_TYPES.ARTICLES,
      report.uuid
    );

    const infobyteData = await getConnectedContentsByName(
      CONTENT_TYPES.INFOBYTES,
      report.uuid
    );

    const spotlightData = await getConnectedContentsByName(
      CONTENT_TYPES.SPOTLIGHTS,
      report.uuid
    );

    const other_reports = reportData?.data?.data || [];
    const videos = videoData?.data?.data || [];
    const articles = articleData?.data?.data || [];
    const infobytes = infobyteData?.data?.data || [];
    const spotlights = spotlightData?.data?.data || [];

    return {
      props: {
        report,
        other_reports,
        videos,
        articles,
        infobytes,
        spotlights,
      },
    };
  } catch (error) {
    // console.log("Error Report", error);
    return {
      notFound: true,
    };
  }
}

export default ReportDetail;
